import {Dialog, Disclosure, Transition} from '@headlessui/react';
import {ChevronRightIcon} from '@heroicons/react/24/solid';
import React from 'react';

import {ForecastTimeSeriesQuery, ObservationTimeSeriesQuery} from 'src/lib/api/series';
import * as tw from 'src/lib/tailwindClasses';

type Props = {
  title: string;
  isVisible: boolean;
  setIsVisible: (isVisible: boolean) => void;
  forecastQueries: ForecastTimeSeriesQuery[];
  observationQueries: ObservationTimeSeriesQuery[];
};

export default function VizQueries({
  title,
  isVisible,
  setIsVisible,
  forecastQueries,
  observationQueries,
}: Props) {
  return (
    <Transition appear show={isVisible} as={React.Fragment}>
      <Dialog as="div" className="relative z-50" onClose={() => setIsVisible(false)}>
        <Transition.Child
          as={React.Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                  API v2 requests for {title}
                </Dialog.Title>
                <div className="text-xs mt-4">
                  <ul>
                    <li>Access your API key in Settings</li>

                    <li>
                      <a
                        className={tw.link}
                        href="https://hydroforecast.com/support/article/194-hydroforecast-time-series-api-v2/"
                        target="_blank"
                        rel="noreferrer"
                      >
                        API v2 Documentation
                      </a>
                    </li>
                  </ul>
                </div>
                <div className="mt-4 space-y-2">
                  {forecastQueries && forecastQueries.length > 0 && (
                    <Section
                      route="forecasts"
                      body={JSON.stringify({queries: forecastQueries}, undefined, 2)}
                    />
                  )}
                  {observationQueries && observationQueries.length > 0 && (
                    <Section
                      route="observations"
                      body={JSON.stringify({queries: observationQueries}, undefined, 2)}
                    />
                  )}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}

const Section = ({route, body}: {route: string; body: string}) => {
  return (
    <Disclosure>
      {({open}) => (
        <>
          <Disclosure.Button className="flex w-full justify-between rounded-lg bg-blue-100 px-4 py-2 text-left text-sm font-medium text-blue-900 hover:bg-blue-200 focus:outline-none focus-visible:ring focus-visible:ring-blue-500 focus-visible:ring-opacity-75">
            <span className="capitalize">{route}</span>
            <ChevronRightIcon
              className={`${open ? 'rotate-90 transform' : ''} h-5 w-5 text-blue-500`}
            />
          </Disclosure.Button>
          <Disclosure.Panel className="px-4 pt-4 pb-2 text-sm text-gray-500 bg-gray-50">
            <dl>
              <dt className="font-bold text-xs">Method</dt>
              <dd className="mt-1 mb-4">
                <pre>POST</pre>
              </dd>
              <dt className="font-bold text-xs">URL</dt>
              <dd className="mt-1 mb-4">
                <pre>https://api.upstream.tech/api/v2/timeseries/{route}</pre>
              </dd>
              <dt className="font-bold text-xs">Header</dt>
              <dd className="mt-1 mb-4">
                <pre>Authorization: &lt;API-Key&gt;</pre>
              </dd>
              <dt className="font-bold text-xs">Body</dt>
              <dd className="mt-1 mb-4">
                <pre>{body}</pre>
              </dd>
            </dl>
          </Disclosure.Panel>
        </>
      )}
    </Disclosure>
  );
};
